import React, { useState } from 'react';
import { Configuration, ConfigurationCategoryType } from '../types';
import { Edit, Trash2, ToggleLeft, ToggleRight, Plus } from 'lucide-react';
import { FadeLoader } from 'react-spinners';

interface ConfigurationListProps {
  configurations: Configuration[];
  category: ConfigurationCategoryType;
  onEdit: (configuration: Configuration) => void;
  onDelete: (category: ConfigurationCategoryType, key: string) => void;
  onToggleStatus: (category: ConfigurationCategoryType, key: string) => void;
  onAddNew: () => void;
  isLoading?: boolean;
}

const ConfigurationList: React.FC<ConfigurationListProps> = ({
  configurations,
  category,
  onEdit,
  onDelete,
  onToggleStatus,
  onAddNew,
  isLoading = false
}) => {
  const [sortBy, setSortBy] = useState<'label' | 'sortOrder' | 'createdAt'>('sortOrder');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter and sort configurations
  const filteredAndSortedConfigurations = configurations
    .filter(config => 
      config.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'label':
          comparison = a.label.localeCompare(b.label);
          break;
        case 'sortOrder':
          comparison = a.sortOrder - b.sortOrder;
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const handleSort = (field: 'label' | 'sortOrder' | 'createdAt') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const formatValue = (value: any): string => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="configuration-list">
      {/* Header with Add Button */}
      <div className="list-header">
        <h3 className="list-title">
          {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Configurations
        </h3>
        <button
          onClick={onAddNew}
          className="btn-primary btn-auto"
        >
          <Plus className="btn-icon" />
          Add New
        </button>
      </div>

      {/* Search and Sort Controls */}
      <div className="list-controls">
        <div className="search-group">
          <input
            type="text"
            placeholder="Search configurations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="sort-controls">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'label' | 'sortOrder' | 'createdAt')}
            className="sort-select"
          >
            <option value="sortOrder">Sort Order</option>
            <option value="label">Label</option>
            <option value="createdAt">Created Date</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="btn-primary btn-small"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Configuration List */}
      <div className="list-container">
        {isLoading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '200px',
            width: '100%'
          }}>
            <FadeLoader 
              color="#1976D2" 
              loading={true} 
              height={15} 
              width={5} 
              radius={2} 
              margin={2}
              speedMultiplier={0.8}
            />
          </div>
        ) : filteredAndSortedConfigurations.length === 0 ? (
          <div className="empty-state">
            <p className="empty-text">
              {searchTerm ? 'No configurations match your search.' : 'No configurations found.'}
            </p>
          </div>
        ) : (
          <ul className="config-list">
            {filteredAndSortedConfigurations.map((config) => (
              <li key={`${config.category}-${config.key}`} className="config-item">
                <div className="config-content">
                  <div className="config-info">
                    <div className="config-header">
                      <div className="config-status">
                        {config.isActive ? (
                          <ToggleRight className="status-icon active" />
                        ) : (
                          <ToggleLeft className="status-icon inactive" />
                        )}
                      </div>
                      <div className="config-details">
                        <div className="config-title-row">
                          <p className="config-label">
                            {config.label}
                          </p>
                          <span className="config-key">
                            {config.key}
                          </span>
                          {!config.isActive && (
                            <span className="status-badge inactive">
                              Inactive
                            </span>
                          )}
                        </div>
                        {config.description && (
                          <p className="config-description">
                            {config.description}
                          </p>
                        )}
                        <div className="config-meta">
                          <span>Value: {formatValue(config.value)}</span>
                          <span>Order: {config.sortOrder}</span>
                          <span>Created: {formatDate(config.createdAt)}</span>
                          {config.updatedBy && (
                            <span>Updated: {formatDate(config.updatedAt)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="config-actions">
                    <button
                      onClick={() => onEdit(config)}
                      className="btn-primary btn-small"
                      title="Edit configuration"
                    >
                      <Edit className="action-icon" />
                    </button>
                    <button
                      onClick={() => onToggleStatus(config.category as ConfigurationCategoryType, config.key)}
                      className="btn-primary btn-small btn-warning"
                      title={config.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {config.isActive ? (
                        <ToggleRight className="action-icon" />
                      ) : (
                        <ToggleLeft className="action-icon" />
                      )}
                    </button>
                    <button
                      onClick={() => onDelete(config.category as ConfigurationCategoryType, config.key)}
                      className="btn-primary btn-small btn-danger"
                      title="Delete configuration"
                    >
                      <Trash2 className="action-icon" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Summary */}
      {filteredAndSortedConfigurations.length > 0 && (
        <div className="list-summary">
          Showing {filteredAndSortedConfigurations.length} of {configurations.length} configurations
        </div>
      )}
    </div>
  );
};

export default ConfigurationList;
